import { useState, useRef, useEffect, type ReactNode } from 'react';

interface DropdownProps {
    buttonClassName?: string;
    buttonContent: ReactNode;
    children: ReactNode;
    className?: string;
    /** Close the menu after any click inside it, for action-menu usage. */
    closeOnItemClick?: boolean;
    disabled?: boolean;
    hideChevron?: boolean;
    menuClassName?: string;
    position?: 'left' | 'right';
}

/**
 * A simple, performant dropdown component using Tailwind CSS
 */
const Dropdown = ({
    buttonClassName = '',
    buttonContent,
    children,
    className = '',
    closeOnItemClick = false,
    disabled = false,
    hideChevron = false,
    menuClassName = '',
    position = 'left'
}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    // Initialize from the requested position so a closed right-aligned menu doesn't render off the
    // right edge (which would extend the page and create a horizontal scrollbar). The open handler
    // re-evaluates available space below.
    const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>(position);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Determine optimal dropdown position based on available space
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            const buttonRect = dropdownRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const dropdownWidth = 224; // w-56 = 14rem = 224px

            // For right-aligned dropdowns, check if dropdown would overflow viewport
            if (position === 'right') {
                const wouldOverflowRight = buttonRect.right > viewportWidth - 20; // 20px margin

                if (wouldOverflowRight || buttonRect.left < dropdownWidth) {
                    // If we're too close to either edge, align left edge with button's left edge
                    setDropdownPosition('left');
                } else {
                    // Otherwise, align right edge with button's right edge
                    setDropdownPosition('right');
                }
            } else {
                // For left-aligned dropdowns, always align left
                setDropdownPosition('left');
            }
        }
    }, [isOpen, position]);

    // Handle clicking outside to close the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        // Add event listener only when dropdown is open
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Clean up event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Handle ESC key to close dropdown
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    return (
        <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
            <button
                aria-expanded={isOpen}
                className={`inline-flex items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`}
                disabled={disabled}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
            >
                {buttonContent}
                {!hideChevron && (
                    <svg aria-hidden="true" className="-mr-1 h-5 w-5 text-zinc-400" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            clipRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            fillRule="evenodd"
                        />
                    </svg>
                )}
            </button>

            <div
                aria-labelledby="menu-button"
                aria-orientation="vertical"
                className={`absolute z-10 mt-2 ${dropdownPosition === 'left' ? 'left-0 origin-top-left' : 'right-0 origin-top-right'} w-56 rounded-md bg-[#1a1a1a] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition-all duration-100 ease-in-out transform ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'} ${menuClassName}`}
                ref={menuRef}
                role="menu"
                tabIndex={-1}
            >
                <div className="py-1" role="none" onClick={closeOnItemClick ? () => setIsOpen(false) : undefined}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Dropdown;
